import './RollingDateRangeFilter.scss'

import { MakeLogicType, actions, kea, key, listeners, path, props, reducers, selectors } from 'kea'

import { Dayjs } from 'lib/dayjs'
import { dateFilterToText } from 'lib/utils/dateFilters'

const dateOptionsMap = {
    y: 'years',
    q: 'quarters',
    m: 'months',
    w: 'weeks',
    d: 'days',
    h: 'hours',
    M: 'minutes',
    s: 'seconds',
} as const

export type DateOption = (typeof dateOptionsMap)[keyof typeof dateOptionsMap]

export type RollingDateFilterLogicPropsType = {
    inUse?: boolean
    onChange?: (fromDate: string) => void
    dateFrom?: Dayjs | string | null
    max?: number | null
    pageKey?: string
}

const counterDefault = (dateFrom: Dayjs | string | null | undefined): number => {
    if (dateFrom && typeof dateFrom === 'string') {
        const counter = parseInt(dateFrom.slice(1, -1))
        if (counter) {
            return counter
        }
    }
    return 3
}

const dateOptionDefault = (dateFrom: Dayjs | string | null | undefined): DateOption => {
    if (dateFrom && typeof dateFrom === 'string') {
        const dateOption = dateOptionsMap[dateFrom.slice(-1) as keyof typeof dateOptionsMap]
        if (dateOption) {
            return dateOption
        }
    }
    return 'days'
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface rollingDateRangeFilterLogicValues {
    counter: number | null
    dateOption: DateOption
    formattedDate: string | null
    isDateOptionsSelectorOpen: boolean
    startOfDateRange: string | null
    value: string
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface rollingDateRangeFilterLogicActions {
    decreaseCounter: () => {
        value: true
    }
    increaseCounter: () => {
        value: true
    }
    select: () => {
        value: true
    }
    setCounter: (counter: number | null | undefined) => {
        counter: number | null | undefined
    }
    setDateOption: (option: DateOption) => {
        option: DateOption
    }
    toggleDateOptionsSelector: () => {
        value: true
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface rollingDateRangeFilterLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        value: (counter: number | null, dateOption: DateOption) => string
        formattedDate: (value: string) => string | null
        startOfDateRange: (value: string) => string | null
    }
}

export type rollingDateRangeFilterLogicType = MakeLogicType<
    rollingDateRangeFilterLogicValues,
    rollingDateRangeFilterLogicActions,
    RollingDateFilterLogicPropsType,
    rollingDateRangeFilterLogicMeta
>

export const rollingDateRangeFilterLogic = kea<rollingDateRangeFilterLogicType>([
    path(['lib', 'components', 'DateFilter', 'RollingDateRangeFilterLogic']),
    props({} as RollingDateFilterLogicPropsType),
    key(({ pageKey }) => pageKey ?? 'unknown'),
    actions({
        increaseCounter: true,
        decreaseCounter: true,
        setCounter: (counter: number | null | undefined) => ({ counter }),
        setDateOption: (option: DateOption) => ({ option }),
        toggleDateOptionsSelector: true,
        select: true,
    }),
    reducers(({ props }) => ({
        counter: [
            counterDefault(props.dateFrom) as number | null,
            {
                increaseCounter: (state) => (state ? (!props.max || state < props.max ? state + 1 : state) : 1),
                decreaseCounter: (state) => {
                    if (state) {
                        return state > 0 ? state - 1 : 0
                    }
                    return 0
                },
                setCounter: (prevCounter, { counter }) => {
                    if (counter) {
                        /** Relative dates must be expressed as integers
                         * @see {isStringDateRegex} */
                        counter = Math.round(counter)
                    }
                    return counter ? (!props.max || counter <= props.max ? counter : prevCounter) : null
                },
            },
        ],
        dateOption: [
            dateOptionDefault(props.dateFrom),
            {
                setDateOption: (_, { option }) => option,
            },
        ],
        isDateOptionsSelectorOpen: [
            false,
            {
                toggleDateOptionsSelector: (state) => !state,
            },
        ],
    })),
    selectors(() => ({
        value: [
            (s) => [s.counter, s.dateOption],
            (counter: number | null, dateOption: DateOption) => {
                if (!counter) {
                    return ''
                }
                switch (dateOption) {
                    case 'years':
                        return `-${counter}y`
                    case 'quarters':
                        return `-${counter}q`
                    case 'months':
                        return `-${counter}m`
                    case 'weeks':
                        return `-${counter}w`
                    case 'days':
                        return `-${counter}d`
                    case 'hours':
                        return `-${counter}h`
                    case 'minutes':
                        return `-${counter}M`
                    case 'seconds':
                        return `-${counter}s`
                    default:
                        return `-${counter}d`
                }
            },
        ],
        formattedDate: [
            (s) => [s.value],
            (value: string) => {
                return dateFilterToText(value, undefined, 'Custom rolling range', [], true)
            },
        ],
        startOfDateRange: [
            (s) => [s.value],
            (value: string) => {
                return dateFilterToText(
                    value,
                    undefined,
                    'N/A',
                    [],
                    false,
                    value.slice(-1) === 'h' ? 'MMMM D, YYYY HH:mm:ss' : 'MMMM D, YYYY',
                    true
                )
            },
        ],
    })),
    listeners(({ props, values, actions }) => ({
        select: async (_val, breakpoint) => {
            await breakpoint(500) // give some extra debounce time, because the menu is fiddly
            props.onChange?.(values.value)
        },
        setDateOption: () => {
            props.onChange?.(values.value)
        },
        setCounter: () => {
            actions.select()
        },
        increaseCounter: () => {
            actions.select()
        },
        decreaseCounter: () => {
            actions.select()
        },
    })),
])
